import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Menu,
  MenuItem,
  Alert,
  Snackbar,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import {
  Search,
  MoreVert,
  Edit,
  Delete,
  Block,
  CheckCircle,
  Visibility,
  Person,
  Gavel,
  Description,
  Refresh,
} from "@mui/icons-material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import logger from "../../utils/logger";

const Tenants = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [actionType, setActionType] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    setLoading(true);
    const token = localStorage.getItem("superAdminToken");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/super-admin/tenants`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTenants(response.data.data);
    } catch (error) {
      logger.error("Error fetching tenants:", error);
      showSnackbar("فشل تحميل المستأجرين", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchTenantDetails = async (tenantId) => {
    const token = localStorage.getItem("superAdminToken");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/super-admin/tenants/${tenantId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedTenant(response.data.data);
      setDetailsDialog(true);
    } catch (error) {
      logger.error("Error fetching tenant details:", error);
      showSnackbar("فشل تحميل تفاصيل المستأجر", "error");
    }
  };

  const handleStatusChange = async () => {
    const token = localStorage.getItem("superAdminToken");
    const newStatus = actionType === "suspend" ? "suspended" : "active";

    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/super-admin/tenants/${selectedTenant.id}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSnackbar(
        `تم ${actionType === "suspend" ? "تعليق" : "تفعيل"} المستأجر بنجاح`,
        "success"
      );
      fetchTenants();
      setConfirmDialog(false);
    } catch (error) {
      logger.error("Error updating status:", error);
      showSnackbar("فشل تحديث حالة المستأجر", "error");
    }
  };

  const handleApproveSubscription = async () => {
    const token = localStorage.getItem("superAdminToken");
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/super-admin/tenants/${selectedTenant.id}/approve-subscription`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSnackbar("تم تأكيد الدفع وتفعيل الاشتراك بنجاح", "success");
      fetchTenants();
      setConfirmDialog(false);
    } catch (error) {
      logger.error("Error approving subscription:", error);
      showSnackbar("فشل تفعيل الاشتراك", "error");
    }
  };

  const handleDelete = async () => {
    const token = localStorage.getItem("superAdminToken");

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/super-admin/tenants/${selectedTenant.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSnackbar("تم حذف المستأجر بنجاح", "success");
      fetchTenants();
      setConfirmDialog(false);
    } catch (error) {
      logger.error("Error deleting tenant:", error);
      showSnackbar("فشل حذف المستأجر", "error");
    }
  };

  const handleMenuOpen = (event, tenant) => {
    setAnchorEl(event.currentTarget);
    setSelectedTenant(tenant);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const openConfirmDialog = (action) => {
    setActionType(action);
    setConfirmDialog(true);
    handleMenuClose();
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "pending_payment":
        return "info";
      case "suspended":
        return "warning";
      case "cancelled":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "active":
        return "نشط";
      case "pending_payment":
        return "بانتظار تأكيد الدفع";
      case "suspended":
        return "معلق";
      case "cancelled":
        return "ملغي";
      default:
        return status;
    }
  };

  const filteredTenants = tenants.filter(
    (tenant) =>
      tenant.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tenant.subdomain?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tenant.contact_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" fontWeight="bold">
          إدارة المستأجرين
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchTenants}
          >
            تحديث
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="البحث في المستأجرين..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {loading ? (
        <LinearProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>اسم المستأجر</strong>
                </TableCell>
                <TableCell>
                  <strong>النطاق الفرعي</strong>
                </TableCell>
                <TableCell>
                  <strong>البريد الإلكتروني</strong>
                </TableCell>
                <TableCell>
                  <strong>الحالة</strong>
                </TableCell>
                <TableCell>
                  <strong>الخطة</strong>
                </TableCell>
                <TableCell>
                  <strong>تاريخ الانضمام</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>الإجراءات</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTenants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary" py={3}>
                      لا توجد مستأجرين
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTenants.map((tenant) => (
                  <TableRow key={tenant.id} hover>
                    <TableCell>{tenant.name}</TableCell>
                    <TableCell>
                      <Chip
                        label={`${tenant.subdomain}.lawsystem.com`}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{tenant.contact_email}</TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(tenant.status)}
                        color={getStatusColor(tenant.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{tenant.plan_name || "غير محدد"}</TableCell>
                    <TableCell>
                      {new Date(tenant.created_at).toLocaleDateString("ar-IQ")}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="عرض التفاصيل">
                        <IconButton
                          size="small"
                          onClick={() => fetchTenantDetails(tenant.id)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, tenant)}
                      >
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => fetchTenantDetails(selectedTenant?.id)}>
          <Visibility fontSize="small" sx={{ mr: 1 }} />
          عرض التفاصيل
        </MenuItem>

        {selectedTenant?.status === "pending_payment" && (
          <MenuItem onClick={() => openConfirmDialog("approve")}>
            <CheckCircle fontSize="small" sx={{ mr: 1, color: "success.main" }} />
            تأكيد الدفع وتفعيل الاشتراك
          </MenuItem>
        )}

        {selectedTenant?.status === "active" ? (
          <MenuItem onClick={() => openConfirmDialog("suspend")}>
            <Block fontSize="small" sx={{ mr: 1 }} />
            تعليق المستأجر
          </MenuItem>
        ) : selectedTenant?.status !== "pending_payment" && (
          <MenuItem onClick={() => openConfirmDialog("activate")}>
            <CheckCircle fontSize="small" sx={{ mr: 1 }} />
            تفعيل المستأجر
          </MenuItem>
        )}
        <MenuItem
          onClick={() => openConfirmDialog("delete")}
          sx={{ color: "error.main" }}
        >
          <Delete fontSize="small" sx={{ mr: 1 }} />
          حذف المستأجر
        </MenuItem>
      </Menu>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialog}
        onClose={() => setDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>تفاصيل المستأجر</DialogTitle>
        <DialogContent>
          {selectedTenant && (
            <Box>
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    اسم المكتب
                  </Typography>
                  <Typography variant="h6">{selectedTenant.name}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    النطاق الفرعي
                  </Typography>
                  <Typography variant="h6">
                    {selectedTenant.subdomain}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    البريد الإلكتروني
                  </Typography>
                  <Typography>{selectedTenant.contact_email}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    الحالة
                  </Typography>
                  <Chip
                    label={getStatusText(selectedTenant.status)}
                    color={getStatusColor(selectedTenant.status)}
                    size="small"
                  />
                </Grid>
              </Grid>

              {selectedTenant.subscription && (
                <Box mb={3}>
                  <Typography variant="h6" gutterBottom>
                    معلومات الاشتراك
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        الخطة الحالية
                      </Typography>
                      <Typography>
                        {selectedTenant.subscription.plan_name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        تاريخ الانتهاء
                      </Typography>
                      <Typography>
                        {new Date(
                          selectedTenant.subscription.end_date
                        ).toLocaleDateString("ar-IQ")}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {selectedTenant.stats && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    الإحصائيات
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: "center" }}>
                          <Person
                            sx={{ fontSize: 40, color: "primary.main", mb: 1 }}
                          />
                          <Typography variant="h6">
                            {selectedTenant.stats.users}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            المستخدمين
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: "center" }}>
                          <Gavel
                            sx={{ fontSize: 40, color: "success.main", mb: 1 }}
                          />
                          <Typography variant="h6">
                            {selectedTenant.stats.cases}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            القضايا
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: "center" }}>
                          <Person
                            sx={{ fontSize: 40, color: "info.main", mb: 1 }}
                          />
                          <Typography variant="h6">
                            {selectedTenant.stats.clients}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            الموكلين
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: "center" }}>
                          <Description
                            sx={{ fontSize: 40, color: "warning.main", mb: 1 }}
                          />
                          <Typography variant="h6">
                            {selectedTenant.stats.documents}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            المستندات
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog(false)}>إغلاق</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>تأكيد الإجراء</DialogTitle>
        <DialogContent>
          <Typography>
            {actionType === "delete"
              ? `هل أنت متأكد من حذف المستأجر "${selectedTenant?.name}"؟ هذا الإجراء لا يمكن التراجع عنه.`
              : actionType === "suspend"
              ? `هل أنت متأكد من تعليق المستأجر "${selectedTenant?.name}"؟`
              : actionType === "approve"
              ? `هل أنت متأكد من تأكيد استلام الدفعة وتفعيل الاشتراك للمستأجر "${selectedTenant?.name}"؟`
              : `هل أنت متأكد من تفعيل المستأجر "${selectedTenant?.name}"؟`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>إلغاء</Button>
          <Button
            onClick={
              actionType === "delete"
                ? handleDelete
                : actionType === "approve"
                ? handleApproveSubscription
                : handleStatusChange
            }
            color={actionType === "delete" ? "error" : "primary"}
            variant="contained"
          >
            تأكيد
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Tenants;
